import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import useApi from "components/Utils/api";
import toast from "react-hot-toast";

const schema = yup.object({
  name: yup.string().required("Route name is required"),
  bankCode: yup.string().nullable(),
  bankName: yup.string().nullable(),
  mode: yup.string().oneOf(["TEST", "UAT", "PROD"]).required("Mode is required"),
  settlementMode: yup.string().oneOf(["INTERNAL", "EXTERNAL"]).required("Settlement mode is required"),
  isDefault: yup.boolean(),
});

export default function SettlementRouteForm() {
  const api = useApi();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { control, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { name: "", bankCode: "", bankName: "", mode: "PROD", settlementMode: "INTERNAL", isDefault: false },
  });

  useEffect(() => {
    if (isEdit) {
      api.get(`settlement-routes/${id}`)
        .then((res) => reset(res.data))
        .catch(() => toast.error("Failed to load route"));
    }
  }, [id]);

  const onSubmit = async (data) => {
    try {
      if (isEdit) {
        await api.put(`settlement-routes/${id}`, data);
        toast.success("Route updated");
      } else {
        await api.post("settlement-routes", data);
        toast.success("Route created");
      }
      navigate("/settlement-routes");
    } catch {
      toast.error("Failed to save route");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h5" mb={3}>
              {isEdit ? "Edit Settlement Route" : "New Settlement Route"}
            </MDTypography>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <MDInput
                        {...field}
                        label="Route Name *"
                        fullWidth
                        error={!!errors.name}
                        helperText={errors.name?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="bankCode"
                    control={control}
                    render={({ field }) => (
                      <MDInput {...field} label="Bank Code" fullWidth />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="bankName"
                    control={control}
                    render={({ field }) => (
                      <MDInput {...field} label="Bank Name" fullWidth />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="mode-label">Mode *</InputLabel>
                    <Controller
                      name="mode"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          labelId="mode-label"
                          label="Mode *"
                          error={!!errors.mode}
                        >
                          <MenuItem value="">- Select Mode -</MenuItem>
                          <MenuItem value="TEST">TEST</MenuItem>
                          <MenuItem value="UAT">UAT</MenuItem>
                          <MenuItem value="PROD">PROD</MenuItem>
                        </Select>
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={8}>
                  <FormControl fullWidth>
                    <InputLabel id="settlement-mode-label">Settlement Mode *</InputLabel>
                    <Controller
                      name="settlementMode"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          labelId="settlement-mode-label"
                          label="Settlement Mode *"
                          error={!!errors.settlementMode}
                        >
                          <MenuItem value="INTERNAL">INTERNAL — TelOne settles via CBZ nodal accounts</MenuItem>
                          <MenuItem value="EXTERNAL">EXTERNAL — PSP settles directly to merchants</MenuItem>
                        </Select>
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6} display="flex" alignItems="center">
                  <Controller
                    name="isDefault"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Set as Default Route"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} display="flex" gap={2} justifyContent="flex-end">
                  <MDButton variant="outlined" color="secondary" onClick={() => navigate("/settlement-routes")}>
                    Cancel
                  </MDButton>
                  <MDButton type="submit" variant="gradient" color="info" disabled={isSubmitting}>
                    {isEdit ? "Update Route" : "Create Route"}
                  </MDButton>
                </Grid>
              </Grid>
            </form>
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
